import { makeStyles } from "@material-ui/core/styles";
import {
  HEADER_TOP_PADDING,
  HEADER_BOTTOM_PADDING,
  HEADER_BORDER_WIDTH,
  HEADER_HEIGHT,
} from "../../../utils/tableExtensionView";

/**
 * Shared table styling hook to reduce duplication across table components.
 * Provides common styles for headers, rows, and typography.
 *
 * @param {Object} options - Configuration options for customizing styles
 * @param {boolean} options.withExtensionBorder - Add 5px transparent left border for extension compensation (default: true)
 * @param {string} options.rowPaddingVertical - Vertical padding for rows (default: "0.8rem")
 * @param {string} options.headerPaddingTop - Top padding for header (default: HEADER_TOP_PADDING)
 * @param {string} options.headerPaddingBottom - Bottom padding for header (default: HEADER_BOTTOM_PADDING)
 * @param {boolean} options.withBorderHighlight - Include borderLeftHighlight style (default: true)
 * @returns {Object} Object containing Material-UI classes
 */
const useTableStyles = (options = {}) => {
  const {
    withExtensionBorder = true,
    rowPaddingVertical = "0.8rem",
    headerPaddingTop = HEADER_TOP_PADDING,
    headerPaddingBottom = HEADER_BOTTOM_PADDING,
    withBorderHighlight = true,
  } = options;

  const useStyles = makeStyles(theme => {
    const baseStyles = {
      root: {
        width: "100%",
      },
      headerRow: {
        height: HEADER_HEIGHT,
        marginLeft: "1rem",
        marginRight: "1rem",
        paddingTop: headerPaddingTop,
        paddingBottom: headerPaddingBottom,
        borderBottom: `solid ${HEADER_BORDER_WIDTH}px #e7e7e7`,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        ...(withExtensionBorder && {
          borderLeft: "5px solid transparent",
          paddingLeft: "0.2rem",
          paddingRight: "0.2rem",
        }),
      },
      productRow: {
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.08)",
        },
        marginLeft: "1rem",
        marginRight: "1rem",
        paddingTop: rowPaddingVertical,
        paddingBottom: rowPaddingVertical,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        borderBottom: "solid 1px #e7e7e7",
        ...(withExtensionBorder && {
          borderLeft: "5px solid transparent",
          paddingLeft: "0.2rem",
          paddingRight: "0.2rem",
        }),
      },
      rowDisabled: {
        backgroundColor: "rgba(0, 0, 0, 0.08)",
      },
      columnHeaderText: {
        color: theme.palette.text.disabled,
        ...theme.typography.button,
        textTransform: "none",
      },
      columnValueText: {
        color: theme.palette.text.primary,
        ...theme.typography.button,
        textTransform: "none",
      },
    };

    if (withBorderHighlight) {
      baseStyles.borderLeftHighlight = {
        borderLeftWidth: "5px",
        borderLeftStyle: "solid",
        borderLeftColor: theme.palette.primary.main,
      };
    }

    return baseStyles;
  });

  return useStyles();
};

export default useTableStyles;
